import {
  InfoBox,
  createFontLoader,
  DesignStudioParametersToolkit,
  PageContainer,
  Loader,
} from "@tfg/ui"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { AGECResponses } from "@tfg/types/endpoints/agec"
import { ProductSheetComponent } from "@tfg/types/schemas/communication-studio/components"
import { ProductSheetLanguage } from "@tfg/types/schemas/languages"
import { ScopedConfigurationsResponse } from "@tfg/types/types/communication-studio/configurations.types"
import { StylingParameters } from "@tfg/types/types/communication-studio/styling-parameters.types"

import { FONT_CONFIG } from "~/utils/fonts"
import { useLanguageNames } from "~/utils/languages"

const expectedMessageTypes = new Set([
  "AGEC_DATA",
  "PRODUCT_PAGE_COMPONENTS",
  "STYLE_DATA",
  "CONFIGURATION_DATA",
  "ACTIVE_LANGUAGES",
])

type ConfigurationData = {
  agecData: AGECResponses.GenericAgecData | null
  styleData: StylingParameters | null
  configuration: ScopedConfigurationsResponse | null
  productPageComponents: ProductSheetComponent[] | null
  activeLanguages: ProductSheetLanguage[]
}

export const ProductSheetPreview = () => {
  const { i18n } = useTranslation()

  const [language, setLanguage] = useState<string>(i18n.language)

  const { getLanguageName } = useLanguageNames(language)

  const { t } = useTranslation("productSheets", {
    lng: language,
  })

  const [configurationData, setConfigurationData] = useState<ConfigurationData>(
    {
      agecData: null,
      styleData: null,
      configuration: null,
      productPageComponents: null,
      activeLanguages: [],
    }
  )
  const [loading, setLoading] = useState(true)
  const [receivedMessages, setReceivedMessages] = useState<Set<string>>(
    new Set()
  )

  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.origin !== import.meta.env.VITE_BASE_FRONT_END_URL) {
      console.error("Message from unauthorized origin:", event.origin)
      return
    }

    const messageType = event.data.type
    const payload = event.data.payload

    // Update received messages set
    setReceivedMessages((prev) => new Set([...prev, messageType]))

    // Update data state based on message type
    switch (messageType) {
      case "AGEC_DATA":
        setConfigurationData((prev) => ({
          ...prev,
          agecData: payload as AGECResponses.GenericAgecData,
        }))
        break
      case "PRODUCT_PAGE_COMPONENTS":
        setConfigurationData((prev) => ({
          ...prev,
          productPageComponents: payload as ProductSheetComponent[],
        }))
        break
      case "STYLE_DATA":
        setConfigurationData((prev) => ({
          ...prev,
          styleData: payload as StylingParameters,
        }))
        break
      case "CONFIGURATION_DATA":
        setConfigurationData((prev) => ({
          ...prev,
          configuration: payload,
        }))
        break
      case "ACTIVE_LANGUAGES":
        setConfigurationData((prev) => ({
          ...prev,
          activeLanguages: payload as ProductSheetLanguage[],
        }))
        break
      default:
        if (process.env.NODE_ENV === "development") {
          console.error("Unknown message type:", messageType)
        }
    }
  }, [])

  useEffect(() => {
    window.addEventListener("message", handleMessage)

    window.parent.postMessage({ type: "IFRAME_READY" }, "*")

    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [handleMessage])

  useEffect(() => {
    const hasAllMessages = [...expectedMessageTypes].every((type) =>
      receivedMessages.has(type)
    )

    if (hasAllMessages) {
      setLoading(false)
    }
  }, [receivedMessages])

  useEffect(() => {
    if (
      !configurationData.activeLanguages ||
      !configurationData.activeLanguages.length ||
      configurationData.activeLanguages.includes(language.toUpperCase())
    ) {
      return
    }

    setLanguage(configurationData.activeLanguages[0].toLocaleLowerCase())
  }, [configurationData.activeLanguages, language])

  const fontFamily = configurationData.styleData
    ? DesignStudioParametersToolkit.extractFontFamily(
        configurationData.styleData
      )
    : []
  const useFontLoader = createFontLoader("app-front", FONT_CONFIG)
  useFontLoader(fontFamily)

  if (loading) {
    return <Loader fullPage />
  }

  const { agecData, styleData, configuration, productPageComponents } =
    configurationData

  if (!agecData || !styleData || !configuration) {
    return (
      <InfoBox
        title="Missing data"
        variant="error"
        message="Please check your data and try again."
      />
    )
  }

  if (!productPageComponents || productPageComponents.length === 0) {
    return (
      <InfoBox
        title="No components"
        variant="warning"
        message="No product page components are available to display."
      />
    )
  }

  return (
    <PageContainer
      agecData={agecData}
      onLanguageChange={setLanguage}
      translationFunction={
        t as unknown as (
          key: string,
          options?: Record<string, string>
        ) => string
      }
      currentLanguage={language}
      stylingParameters={styleData}
      configuration={configuration}
      activeLanguages={configurationData.activeLanguages}
      productPageComponents={productPageComponents ?? []}
      getLanguageName={getLanguageName}
    />
  )
}
